import {
  Children,
  isValidElement,
  type ReactNode,
  useMemo,
} from "react";
import type { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import type { SelectChangeEvent, SelectProps } from "@mui/material";
import AppAutocomplete from "./AppAutocomplete";
import AppTextField from "./AppTextField";
import { APP_FORM_DEFAULTS } from "./constants";

type SelectOption = {
  value: string;
  label: ReactNode;
  disabled?: boolean;
};

type Props = SelectProps & {
  label: string;
  helperText?: string;
  hiddenLabel?: boolean;
  /** Kept for API compatibility; ignored — AppSelect is now an Autocomplete. */
  formControlProps?: unknown;
  selectSlotProps?: SelectProps;
};

function extractOptions(children: ReactNode): SelectOption[] {
  const options: SelectOption[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement<{ value?: unknown; children?: ReactNode; disabled?: boolean }>(child)) {
      return;
    }
    if (child.props.value === undefined) return;
    options.push({
      value: String(child.props.value),
      label: child.props.children,
      disabled: child.props.disabled,
    });
  });
  return options;
}

function optionLabel(option: SelectOption | string): string {
  if (typeof option === "string") return option;
  if (typeof option.label === "string" || typeof option.label === "number") {
    return String(option.label);
  }
  return option.value;
}

/**
 * Autocomplete dropdown with the legacy AppSelect (MenuItem children) API.
 * Prefer AppAutocomplete directly for new code.
 */
export default function AppSelect({
  label,
  helperText,
  hiddenLabel,
  children,
  value,
  onChange,
  disabled,
  name,
  id,
  sx,
  className,
  size,
  fullWidth,
  error,
  multiple,
}: Props) {
  const options = useMemo(() => extractOptions(children), [children]);
  const fieldSize = size ?? APP_FORM_DEFAULTS.size;
  const fieldFullWidth = fullWidth ?? APP_FORM_DEFAULTS.fullWidth;

  const selected = useMemo(() => {
    if (multiple) {
      const values = Array.isArray(value) ? value.map(String) : [];
      return options.filter((o) => values.includes(o.value));
    }
    const raw = value === undefined || value === null ? "" : String(value);
    return options.find((o) => o.value === raw) ?? null;
  }, [multiple, options, value]);

  const emitChange = (next: string | string[]) => {
    if (!onChange) return;
    const event = {
      target: { value: next, name: name ?? "" },
    } as SelectChangeEvent<unknown>;
    onChange(event, undefined as never);
  };

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <AppTextField
      {...params}
      label={hiddenLabel ? undefined : label}
      hiddenLabel={hiddenLabel}
      helperText={helperText}
      margin={APP_FORM_DEFAULTS.margin}
      size={fieldSize}
      name={name}
      id={id}
      error={error}
    />
  );

  if (multiple) {
    const multiValue = Array.isArray(selected) ? selected : [];
    return (
      <AppAutocomplete<SelectOption, true>
        multiple
        label={hiddenLabel ? "" : label}
        helperText={helperText}
        options={options}
        value={multiValue}
        disabled={disabled}
        size={fieldSize}
        fullWidth={fieldFullWidth}
        getOptionLabel={optionLabel}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        getOptionDisabled={(o) => Boolean(o.disabled)}
        onChange={(_, next) => emitChange(next.map((o) => o.value))}
        sx={sx}
        className={className}
        renderInput={renderInput}
      />
    );
  }

  const singleValue = Array.isArray(selected) ? null : selected;

  return (
    <AppAutocomplete<SelectOption>
      label={hiddenLabel ? "" : label}
      helperText={helperText}
      options={options}
      value={singleValue}
      disabled={disabled}
      size={fieldSize}
      fullWidth={fieldFullWidth}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      getOptionDisabled={(o) => Boolean(o.disabled)}
      onChange={(_, next) => emitChange(next?.value ?? "")}
      sx={sx}
      className={className}
      renderInput={renderInput}
    />
  );
}
